import { Dialog, DialogContent, DialogHeader, DialogTitle,
         DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger,
         SelectValue } from "@/components/ui/select";
import { Plus, Repeat } from "lucide-react";
import { HOUR_OPTIONS } from "./constants";
import { SmsPreviewPanel } from "./SmsPreviewPanel";

const DOW_LETTERS = [["M", 0], ["T", 1], ["W", 2], ["T", 3],
                      ["F", 4], ["S", 5], ["S", 6]];

/**
 * The full "Issue guest parking pass" form + trigger. Owns *only* form UI —
 * validation + POST live in the parent so the dialog is straight-through.
 */
export function IssuePassDialog({ open, onOpenChange, form, setForm, busy, onSubmit }) {
  const setField = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggleDayOfWeek = (dow) => {
    setForm((f) => ({
      ...f,
      days_of_week: f.days_of_week.includes(dow)
        ? f.days_of_week.filter((d) => d !== dow)
        : [...f.days_of_week, dow].sort(),
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="rounded-full bg-orange-500 hover:bg-orange-600 text-black"
                data-testid="issue-guest-pass-btn">
          <Plus className="h-4 w-4 mr-1" /> Issue pass
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="issue-pass-dialog">
        <DialogHeader><DialogTitle>Issue guest parking pass</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <Label>Guest name *</Label>
            <Input data-testid="guest-name" value={form.guest_name}
                   onChange={(e) => setField({ guest_name: e.target.value })}
                   placeholder="Jamie Rivera" />
          </div>
          <div>
            <Label>License plate *</Label>
            <Input data-testid="guest-plate" value={form.plate}
                   onChange={(e) => setField({ plate: e.target.value.toUpperCase() })}
                   placeholder="ABC-1234" className="font-mono uppercase" />
          </div>
          <div>
            <Label>Vehicle description</Label>
            <Input data-testid="guest-vehicle" value={form.vehicle_desc}
                   onChange={(e) => setField({ vehicle_desc: e.target.value })}
                   placeholder="Blue Honda Civic" />
          </div>
          <div>
            <Label>Valid for</Label>
            <Select value={form.hours} onValueChange={(v) => setField({ hours: v })}>
              <SelectTrigger data-testid="guest-hours"><SelectValue /></SelectTrigger>
              <SelectContent>
                {HOUR_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label>Notes</Label>
            <Input data-testid="guest-notes" value={form.notes}
                   onChange={(e) => setField({ notes: e.target.value })}
                   placeholder="Family visiting for the weekend" />
          </div>
          <div className="md:col-span-2">
            <Label className="flex items-center gap-1">
              Guest phone <span className="text-zinc-500 text-[10px]">(optional — we text them the QR + address when it activates)</span>
            </Label>
            <Input type="tel" data-testid="guest-phone" value={form.guest_phone}
                   onChange={(e) => setField({ guest_phone: e.target.value })}
                   placeholder="(555) 123-4567" inputMode="tel" />
          </div>
          <div className="md:col-span-2">
            <Label className="flex items-center justify-between">
              <span>Custom note in SMS <span className="text-zinc-500 text-[10px]">(optional — e.g. gate code, unit number)</span></span>
              <span className="text-[10px] text-zinc-500 tabular-nums">{form.custom_note.length}/120</span>
            </Label>
            <Input data-testid="guest-custom-note" value={form.custom_note}
                   onChange={(e) => setField({ custom_note: e.target.value.slice(0, 120) })}
                   placeholder="Gate code is 4232 · park in visitor spots by the pool" />
          </div>
          <SmsPreviewPanel form={form} />
          <div className="md:col-span-2">
            <Label className="flex items-center justify-between">
              <span>Schedule for later <span className="text-zinc-500 text-[10px]">(optional)</span></span>
              {form.scheduled_start_at && (
                <button type="button" className="text-[10px] text-orange-500 hover:text-orange-600"
                        onClick={() => setField({ scheduled_start_at: "" })}>
                  clear
                </button>
              )}
            </Label>
            <Input type="datetime-local" data-testid="guest-scheduled-at"
                   value={form.scheduled_start_at}
                   disabled={form.recurring}
                   onChange={(e) => setField({ scheduled_start_at: e.target.value })}
                   min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                   max={new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 16)} />
            <div className="text-[10px] text-zinc-500 mt-1">
              Leave empty to activate right now. Otherwise the pass activates at that time and the {form.hours}h window starts then.
            </div>
          </div>

          <div className="md:col-span-2 rounded-lg border border-white/10 bg-white/[0.02] p-3">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="recurring-toggle">
              <input type="checkbox" checked={form.recurring}
                     onChange={(e) => setField({ recurring: e.target.checked, scheduled_start_at: "" })}
                     className="h-4 w-4 accent-orange-500" />
              <Repeat className="h-4 w-4 text-orange-400" />
              <span className="text-sm font-medium">Make it recurring</span>
              <span className="text-[10px] text-zinc-500 ml-auto">housekeepers, weekly cleaners, monthly visits</span>
            </label>
            {form.recurring && (
              <div className="mt-3 space-y-3 pl-6">
                <div className="flex gap-2">
                  <button type="button" onClick={() => setField({ pattern: "weekly" })}
                          data-testid="recurring-pattern-weekly"
                          className={"flex-1 px-3 py-2 text-xs rounded-md border transition " +
                            (form.pattern === "weekly"
                              ? "bg-orange-500 border-orange-500 text-white"
                              : "bg-transparent border-white/10 text-zinc-400 hover:border-orange-300")}>
                    Weekly
                  </button>
                  <button type="button" onClick={() => setField({ pattern: "monthly" })}
                          data-testid="recurring-pattern-monthly"
                          className={"flex-1 px-3 py-2 text-xs rounded-md border transition " +
                            (form.pattern === "monthly"
                              ? "bg-orange-500 border-orange-500 text-white"
                              : "bg-transparent border-white/10 text-zinc-400 hover:border-orange-300")}>
                    Monthly
                  </button>
                </div>
                {form.pattern === "weekly" && (
                  <div>
                    <Label className="text-[10px] text-zinc-500">Days of week</Label>
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {DOW_LETTERS.map(([label, dow]) => (
                        <button key={dow} type="button" onClick={() => toggleDayOfWeek(dow)}
                                data-testid={`recurring-dow-${dow}`}
                                className={"h-8 w-8 rounded-md text-xs font-medium border transition " +
                                  (form.days_of_week.includes(dow)
                                    ? "bg-orange-500 border-orange-500 text-white"
                                    : "bg-transparent border-white/10 text-zinc-400 hover:border-orange-300")}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {form.pattern === "monthly" && (
                  <div>
                    <Label className="text-[10px] text-zinc-500">Day of the month (1–28)</Label>
                    <Input type="number" min={1} max={28} value={form.day_of_month}
                           onChange={(e) => setField({ day_of_month: e.target.value })}
                           data-testid="recurring-day-of-month" className="h-8 w-20" />
                    <div className="text-[10px] text-zinc-500 mt-1">
                      Capped at 28 so months with fewer days still work.
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-[10px] text-zinc-500">Time</Label>
                    <Input type="time" value={form.time_of_day}
                           onChange={(e) => setField({ time_of_day: e.target.value })}
                           data-testid="recurring-time" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-zinc-500">Starts on</Label>
                    <Input type="date" value={form.starts_on}
                           onChange={(e) => setField({ starts_on: e.target.value })}
                           min={new Date().toISOString().slice(0, 10)}
                           data-testid="recurring-starts-on" />
                  </div>
                  <div>
                    <Label className="text-[10px] text-zinc-500">Ends on <span className="opacity-60">(opt)</span></Label>
                    <Input type="date" value={form.ends_on}
                           onChange={(e) => setField({ ends_on: e.target.value })}
                           min={form.starts_on}
                           data-testid="recurring-ends-on" />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  data-testid="issue-pass-cancel">Cancel</Button>
          <Button onClick={onSubmit} disabled={busy}
                  data-testid={form.recurring ? "issue-series-btn" : "issue-pass-submit"}
                  className="rounded-full bg-orange-500 hover:bg-orange-600 text-black">
            {busy ? "Saving…" : (form.recurring ? "Schedule series" : "Issue pass")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
